import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";

export default function About() {
    return (
        <div data-testid="page-about">
            <Seo
                title="About | Chief AI Partners"
                description="Chief AI Partners takes responsibility for AI inside mid-market organisations. Built on real business and operational experience across Australia, New Zealand and the Pacific."
                path="/about"
            />

            <section className="cap-section pt-24 md:pt-32 pb-16" data-testid="about-hero">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8">
                        <div className="cap-eyebrow mb-6">About</div>
                        <h1 className="font-serif text-navy text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                            AI ownership for businesses that need execution.
                        </h1>
                    </div>
                    <div className="lg:col-span-4 lg:pt-4 space-y-5">
                        <p className="text-slate text-lg leading-relaxed">
                            Chief AI Partners exists to take responsibility for AI inside a business.
                        </p>
                        <p className="text-navy text-lg font-medium leading-relaxed">
                            Not to advise. Not to experiment. To implement and run it.
                        </p>
                        <p className="text-slate text-lg leading-relaxed">
                            We work with organisations that have moved beyond interest and are ready to apply structured systems to real operations.
                        </p>
                    </div>
                </div>
                <div className="cap-container mt-12"><div className="cap-rule" /></div>
            </section>

            <section className="cap-section bg-sand/40 border-y border-sand" data-testid="about-experience">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="cap-eyebrow mb-4">Experience</div>
                        <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                            Built on real business and operational experience.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-5">
                        <p className="text-slate text-lg leading-relaxed">
                            Chief AI Partners is grounded in decades of hands-on business leadership across operations, sales and management.
                        </p>
                        <p className="text-slate text-lg leading-relaxed">
                            We have worked inside organisations at multiple levels, from execution through to senior leadership, with direct responsibility for:
                        </p>
                        <ul className="divide-y divide-sand border-y border-sand">
                            {[
                                "Revenue growth and sales performance",
                                "Operational efficiency and process design",
                                "Team management and scaling",
                                "Systems implementation and workflow optimisation",
                            ].map((item, i) => (
                                <li
                                    key={item}
                                    className="flex items-start gap-4 py-4"
                                    data-testid={`about-experience-${i}`}
                                >
                                    <span className="text-xs uppercase tracking-[0.22em] text-teal font-semibold pt-1.5 min-w-[2.5rem]">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="text-navy text-base md:text-lg leading-relaxed">{item}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-navy text-lg font-medium leading-relaxed">
                            This is not theoretical experience.
                        </p>
                        <p className="text-slate text-lg leading-relaxed">
                            It is built from running businesses, managing teams and being accountable for results.
                        </p>
                    </div>
                </div>
            </section>

            <section className="cap-section" data-testid="about-belief">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="cap-eyebrow mb-4">Our belief</div>
                        <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                            Capacity, not replacement.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-5">
                        <p className="text-slate text-lg leading-relaxed">
                            AI is not about replacing people.
                        </p>
                        <p className="text-slate text-lg leading-relaxed">
                            It is about removing repetitive work, improving consistency and giving teams the capacity to focus on higher-value activity.
                        </p>
                    </div>
                </div>
            </section>

            <section className="cap-section bg-sand/40 border-y border-sand" data-testid="about-approach">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="cap-eyebrow mb-4">Our approach</div>
                        <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                            Practical, measured, continuous.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-5">
                        <p className="text-slate text-lg leading-relaxed">
                            We focus on systems that improve how a business operates.
                        </p>
                        <p className="text-slate text-lg leading-relaxed">
                            We do not believe in strategy without execution, or tools without adoption.
                        </p>
                        <p className="text-slate text-lg leading-relaxed">
                            Every engagement is designed to produce measurable outcomes and build capability inside the business over time.
                        </p>
                    </div>
                </div>
            </section>

            <section className="cap-section" data-testid="about-how-we-work">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="cap-eyebrow mb-4">How we work</div>
                        <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                            Structured. Focused. Outcome-driven.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-5">
                        <p className="text-slate text-lg leading-relaxed">
                            We take a structured approach to understanding how a business operates, identifying where value can be created, and implementing systems that improve performance.
                        </p>
                        <p className="text-slate text-lg leading-relaxed">
                            Our role is to bring clarity, direction and execution into an area that is often fragmented and under-owned.
                        </p>
                    </div>
                </div>
            </section>

            <section className="cap-section bg-sand/40 border-y border-sand" data-testid="about-cta">
                <div className="cap-container text-center max-w-3xl mx-auto">
                    <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                        Work with us.
                    </h2>
                    <p className="mt-6 text-slate text-lg leading-relaxed">
                        Book a short conversation. We'll share how organisations like yours are getting practical value from AI.
                    </p>
                    <Link to="/contact" className="cap-btn-primary mt-8" data-testid="about-cta-book">
                        Book a 15-minute conversation <ArrowRight size={16} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
